// features/inventory/pages/ReviewConfirmPage.tsx
/**
 * Review & Confirm Page — Selective stock confirmation with variance intelligence.
 *
 * Route: /inventory/review
 *
 * Features:
 * - Variance classification: none, minor (<5%), major (5-20%), critical (>20%)
 * - Filterable by variance level and confirmation status
 * - Bulk select with individual checkboxes and select all
 * - Quick confirm individual items inline
 * - Search by product name
 * - Branch selection for multi-store setups
 * - Confirmation progress bar at top
 * - Pagination with configurable page size
 * - Quick actions: view detail, adjust, history
 * - Visual indicators for variance severity
 */

import { useState, useMemo, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuthStore } from '@/store/authStore';
import { useToastStore } from '@/store/toastStore';
import { Card, CardContent } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import { Spinner } from '@/components/ui/Spinner';
import { EmptyState } from '@/components/ui/EmptyState';
import { ErrorState } from '@/components/ui/ErrorState';
import { Pagination } from '@/components/ui/Pagination';
import {
    getStockLevels,
    confirmStock,
    getStockConfirmationStatus,
    StockLevel,
    ConfirmationStatus,
} from '@/features/stock/services/stockApi';
import { getBranches } from '@/features/settings/services/settingsApi';
import {
    Search,
    X,
    CheckCircle,
    AlertTriangle,
    ShieldCheck,
    Eye,
    Package,
    Store,
    TrendingUp,
    TrendingDown,
    CheckCheck,
    XCircle,
    Clock,
    RotateCcw,
    History,
    Filter,
    ArrowLeft,
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { clsx } from 'clsx';

// ============================================================
// TYPES
// ============================================================

type VarianceLevel = 'all' | 'none' | 'minor' | 'major' | 'critical';
type ConfirmationFilter = 'all' | 'pending' | 'counted';

interface ReviewItem extends StockLevel {
    variancePercent: number;
    varianceLevel: VarianceLevel;
    isSelected: boolean;
}

interface VarianceConfig {
    label: string;
    color: string;
    bg: string;
    icon: React.ElementType;
}

// ============================================================
// CONSTANTS
// ============================================================

const PAGE_SIZE = 30;

const VARIANCE_CONFIG: Record<VarianceLevel, VarianceConfig> = {
    all: {
        label: 'All',
        color: 'text-slate-600',
        bg: 'bg-slate-50',
        icon: Filter,
    },
    none: {
        label: 'Exact Match',
        color: 'text-emerald-600',
        bg: 'bg-emerald-50',
        icon: CheckCircle,
    },
    minor: {
        label: 'Minor (<5%)',
        color: 'text-blue-600',
        bg: 'bg-blue-50',
        icon: TrendingUp,
    },
    major: {
        label: 'Major (5-20%)',
        color: 'text-amber-600',
        bg: 'bg-amber-50',
        icon: AlertTriangle,
    },
    critical: {
        label: 'Critical (>20%)',
        color: 'text-rose-600',
        bg: 'bg-rose-50',
        icon: XCircle,
    },
};

// ============================================================
// HELPERS
// ============================================================

function classifyVariance(
    expected: number,
    counted: number
): { percent: number; level: VarianceLevel } {
    if (expected === 0 && counted === 0)
        return { percent: 0, level: 'none' };
    if (expected === 0)
        return { percent: 100, level: 'critical' };
    const pct = Math.abs(((counted - expected) / expected) * 100);
    if (pct === 0) return { percent: 0, level: 'none' };
    if (pct <= 5) return { percent: pct, level: 'minor' };
    if (pct <= 20) return { percent: pct, level: 'major' };
    return { percent: pct, level: 'critical' };
}

// ============================================================
// MAIN COMPONENT
// ============================================================

export function ReviewConfirmPage() {
    const navigate = useNavigate();
    const user = useAuthStore((s) => s.user);
    const selectedBranchId = useAuthStore((s) => s.selectedBranchId);
    const queryClient = useQueryClient();
    const addToast = useToastStore((s) => s.addToast);

    // Filters
    const [branchId, setBranchId] = useState(selectedBranchId || '');
    const [search, setSearch] = useState('');
    const [varianceFilter, setVarianceFilter] = useState<VarianceLevel>('all');
    const [confirmationFilter, setConfirmationFilter] =
        useState<ConfirmationFilter>('counted');
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(PAGE_SIZE);

    // Selection
    const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
    const [selectAll, setSelectAll] = useState(false);

    // Queries
    const { data: branches } = useQuery({
        queryKey: ['branches-review', user?.tenantId],
        queryFn: async () => {
            try {
                const data = await getBranches(user!.tenantId);
                return Array.isArray(data) ? data : [];
            } catch {
                return [];
            }
        },
        enabled: !!user?.tenantId,
    });

    const { data: confirmationStatus, refetch: refetchStatus } =
        useQuery<ConfirmationStatus>({
            queryKey: [
                'confirmation-status-review',
                user?.tenantId,
                branchId,
            ],
            queryFn: () =>
                getStockConfirmationStatus(
                    user!.tenantId,
                    branchId
                ),
            enabled: !!user?.tenantId && !!branchId,
        });

    const {
        data: stockResponse,
        isLoading,
        isFetching,
        isError,
        refetch,
    } = useQuery({
        queryKey: [
            'stock-levels-review',
            user?.tenantId,
            search,
            confirmationFilter,
            branchId,
            page,
            pageSize,
        ],
        queryFn: async () => {
            const response = await getStockLevels(
                user!.tenantId,
                {
                    search: search || undefined,
                    branchId: branchId || undefined,
                    confirmationStatus:
                        confirmationFilter !== 'all'
                            ? confirmationFilter
                            : undefined,
                    limit: pageSize,
                    page,
                    sortBy: 'updatedAt',
                    order: 'desc',
                }
            );
            return response;
        },
        enabled: !!user?.tenantId,
        placeholderData: (prev) => prev,
    });

    // Mutations
    const confirmMutation = useMutation({
        mutationFn: async (ids: string[]) => {
            const productIds = enrichedItems
                .filter(
                    (i) =>
                        ids.includes(i.id) &&
                        i.confirmationStatus !== 'confirmed'
                )
                .map((i) => i.productId);
            if (productIds.length === 0)
                throw new Error('No items to confirm.');
            return confirmStock(
                user!.tenantId,
                branchId,
                productIds
            );
        },
        onSuccess: (result) => {
            addToast({
                type: 'success',
                title: 'Stock Confirmed',
                message: `${result.confirmed} items now sellable.`,
            });
            setSelectedIds(new Set());
            setSelectAll(false);
            queryClient.invalidateQueries({
                queryKey: ['stock-levels-review'],
            });
            queryClient.invalidateQueries({
                queryKey: ['confirm-status-dash'],
            });
            queryClient.invalidateQueries({
                queryKey: ['low-stock-dash'],
            });
            refetchStatus();
        },
        onError: (err: any) =>
            addToast({
                type: 'error',
                title: 'Failed',
                message: err?.message,
            }),
    });

    // Derived data
    const branchesList = (branches || []) as Array<{
        id: string;
        name: string;
    }>;
    const isSingleStore = branchesList.length <= 1;
    const items: StockLevel[] = stockResponse?.data || [];
    const totalCount = stockResponse?.meta?.totalCount || 0;
    const totalPages = Math.max(
        1,
        Math.ceil(totalCount / pageSize)
    );

    // Auto-select branch
    useEffect(() => {
        if (branchesList.length > 0 && !branchId) {
            const preferred =
                selectedBranchId &&
                    branchesList.find(
                        (b: any) => b.id === selectedBranchId
                    )
                    ? selectedBranchId
                    : branchesList.find(
                        (b: any) => b.isMainBranch
                    )?.id ||
                    branchesList[0]?.id ||
                    '';
            setBranchId(preferred);
        }
    }, [branchesList, selectedBranchId, branchId]);

    // Enrich items with variance data
    const enrichedItems: ReviewItem[] = useMemo(() => {
        return items.map((item) => {
            const onHand = parseFloat(
                item.quantityOnHand || '0'
            );
            const available = parseFloat(
                item.availableQuantity || '0'
            );
            const { percent, level } = classifyVariance(
                onHand,
                available
            );
            const isConfirmed =
                item.confirmationStatus === 'confirmed';
            return {
                ...item,
                variancePercent: percent,
                varianceLevel: level,
                isSelected:
                    !isConfirmed && selectedIds.has(item.id),
            };
        });
    }, [items, selectedIds]);

    // Filter by variance level
    const filteredItems = useMemo(() => {
        if (varianceFilter === 'all') return enrichedItems;
        return enrichedItems.filter(
            (item) => item.varianceLevel === varianceFilter
        );
    }, [enrichedItems, varianceFilter]);

    // Selectable items (not confirmed)
    const selectableItems = useMemo(() => {
        return filteredItems.filter(
            (i) => i.confirmationStatus !== 'confirmed'
        );
    }, [filteredItems]);

    // Stats
    const stats = useMemo(() => {
        return {
            total: enrichedItems.length,
            exactMatch: enrichedItems.filter(
                (i) => i.varianceLevel === 'none'
            ).length,
            minor: enrichedItems.filter(
                (i) => i.varianceLevel === 'minor'
            ).length,
            major: enrichedItems.filter(
                (i) => i.varianceLevel === 'major'
            ).length,
            critical: enrichedItems.filter(
                (i) => i.varianceLevel === 'critical'
            ).length,
            confirmed: enrichedItems.filter(
                (i) => i.confirmationStatus === 'confirmed'
            ).length,
            selectable: selectableItems.length,
            selected: selectedIds.size,
        };
    }, [enrichedItems, selectableItems, selectedIds]);

    // Handlers
    const handleSelectAll = useCallback(() => {
        if (selectAll) {
            setSelectedIds(new Set());
            setSelectAll(false);
        } else {
            setSelectedIds(
                new Set(selectableItems.map((i) => i.id))
            );
            setSelectAll(true);
        }
    }, [selectAll, selectableItems]);

    const handleSelectOne = useCallback(
        (id: string, isConfirmed: boolean) => {
            if (isConfirmed) return;
            const newSet = new Set(selectedIds);
            if (newSet.has(id)) {
                newSet.delete(id);
                setSelectAll(false);
            } else {
                newSet.add(id);
                if (newSet.size === selectableItems.length)
                    setSelectAll(true);
            }
            setSelectedIds(newSet);
        },
        [selectedIds, selectableItems]
    );

    const handleConfirmSelected = useCallback(() => {
        if (selectedIds.size === 0) {
            addToast({
                type: 'warning',
                title: 'Nothing selected',
                message:
                    'Select items to confirm first.',
            });
            return;
        }
        confirmMutation.mutate(Array.from(selectedIds));
    }, [selectedIds, confirmMutation, addToast]);

    const handleQuickConfirm = useCallback(
        (id: string) => {
            confirmMutation.mutate([id]);
        },
        [confirmMutation]
    );

    const handlePageChange = useCallback(
        (newPage: number) => {
            if (
                newPage < 1 ||
                newPage > totalPages ||
                newPage === page
            )
                return;
            setPage(newPage);
            window.scrollTo({ top: 0, behavior: 'smooth' });
        },
        [totalPages, page]
    );

    const formatNumber = (val: string | undefined): string => {
        if (!val) return '0';
        const num = parseFloat(val);
        return isNaN(num)
            ? '0'
            : num.toLocaleString(undefined, {
                maximumFractionDigits: 0,
            });
    };

    const getBranchName = (id: string) =>
        branchesList.find((b) => b.id === id)?.name || 'Unknown';

    const confirmedPercent =
        confirmationStatus && confirmationStatus.total > 0
            ? Math.round(
                (confirmationStatus.confirmed /
                    confirmationStatus.total) *
                100
            )
            : 0;
    const countedPercent =
        confirmationStatus && confirmationStatus.total > 0
            ? Math.round(
                (confirmationStatus.counted /
                    confirmationStatus.total) *
                100
            )
            : 0;
    const pendingPercent =
        confirmationStatus && confirmationStatus.total > 0
            ? Math.round(
                (confirmationStatus.pending /
                    confirmationStatus.total) *
                100
            )
            : 0;

    // ============================================================
    // RENDER
    // ============================================================

    return (
        <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-emerald-50/30">
            {/* Header */}
            <div className="bg-white/90 backdrop-blur-md border-b border-slate-200 sticky top-0 z-30 shadow-sm">
                <div className="max-w-[1600px] mx-auto px-6 lg:px-8 py-4">
                    <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
                        <div className="flex items-center gap-4">
                            <button
                                onClick={() =>
                                    navigate('/inventory')
                                }
                                className="p-2 rounded-xl hover:bg-slate-100 transition-colors"
                                type="button"
                            >
                                <ArrowLeft className="w-5 h-5 text-slate-600" />
                            </button>
                            <div className="p-2.5 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-2xl shadow-lg shadow-blue-500/25">
                                <ShieldCheck className="w-6 h-6 text-white" />
                            </div>
                            <div>
                                <h1 className="text-2xl font-bold text-slate-900">
                                    Review & Confirm
                                </h1>
                                <p className="text-sm text-slate-500">
                                    {getBranchName(branchId)} ·{' '}
                                    {stats.total} items ·{' '}
                                    {stats.selectable} need
                                    review
                                </p>
                            </div>
                        </div>
                        <div className="flex items-center gap-2">
                            <button
                                onClick={() => refetch()}
                                disabled={isFetching}
                                className="flex items-center gap-2 px-4 py-2.5 bg-white border border-slate-200 rounded-xl hover:bg-slate-50 transition-all text-sm font-medium text-slate-700 shadow-sm"
                                type="button"
                            >
                                <RotateCcw
                                    className={clsx(
                                        'w-4 h-4',
                                        isFetching &&
                                        'animate-spin'
                                    )}
                                />{' '}
                                Refresh
                            </button>
                            {stats.selected > 0 && (
                                <motion.button
                                    initial={{
                                        scale: 0,
                                        opacity: 0,
                                    }}
                                    animate={{
                                        scale: 1,
                                        opacity: 1,
                                    }}
                                    whileHover={{
                                        scale: 1.03,
                                    }}
                                    whileTap={{
                                        scale: 0.97,
                                    }}
                                    onClick={
                                        handleConfirmSelected
                                    }
                                    disabled={
                                        confirmMutation.isPending
                                    }
                                    className="flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-emerald-600 to-teal-600 text-white rounded-xl hover:from-emerald-700 transition-all font-bold shadow-lg shadow-emerald-500/25 text-sm disabled:opacity-50"
                                    type="button"
                                >
                                    {confirmMutation.isPending ? (
                                        <Spinner
                                            size="xs"
                                            variant="white"
                                        />
                                    ) : (
                                        <CheckCheck className="w-4 h-4" />
                                    )}
                                    Confirm{' '}
                                    {stats.selected} Selected
                                </motion.button>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            <div className="max-w-[1600px] mx-auto p-6 lg:p-8 space-y-5">
                {/* Confirmation Progress Bar */}
                {confirmationStatus && (
                    <motion.div
                        initial={{ opacity: 0, y: -10 }}
                        animate={{ opacity: 1, y: 0 }}
                    >
                        <Card className="bg-gradient-to-r from-blue-50 to-indigo-50 border-2 border-blue-200 overflow-hidden">
                            <CardContent className="p-4">
                                <div className="flex items-center justify-between mb-3">
                                    <div className="flex items-center gap-3">
                                        <div className="p-2 rounded-xl bg-blue-100">
                                            <ShieldCheck className="w-5 h-5 text-blue-600" />
                                        </div>
                                        <div>
                                            <h3 className="font-bold text-blue-800">
                                                Confirmation
                                                Progress
                                            </h3>
                                            <p className="text-sm text-blue-600">
                                                {
                                                    confirmationStatus.total
                                                }{' '}
                                                total ·{' '}
                                                {
                                                    confirmationStatus.confirmed
                                                }{' '}
                                                sellable
                                            </p>
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-4 text-xs font-medium">
                                        <span className="flex items-center gap-1.5">
                                            <span className="w-2.5 h-2.5 rounded-full bg-amber-500" />{' '}
                                            {
                                                confirmationStatus.pending
                                            }{' '}
                                            Pending
                                        </span>
                                        <span className="flex items-center gap-1.5">
                                            <span className="w-2.5 h-2.5 rounded-full bg-blue-500" />{' '}
                                            {
                                                confirmationStatus.counted
                                            }{' '}
                                            Counted
                                        </span>
                                        <span className="flex items-center gap-1.5">
                                            <span className="w-2.5 h-2.5 rounded-full bg-emerald-500" />{' '}
                                            {
                                                confirmationStatus.confirmed
                                            }{' '}
                                            Confirmed
                                        </span>
                                    </div>
                                </div>
                                <div className="h-2.5 bg-blue-100 rounded-full overflow-hidden">
                                    <div className="h-full flex rounded-full overflow-hidden">
                                        <motion.div
                                            className="bg-emerald-500"
                                            initial={{
                                                width: 0,
                                            }}
                                            animate={{
                                                width: `${confirmedPercent}%`,
                                            }}
                                            transition={{
                                                duration: 1,
                                            }}
                                        />
                                        <motion.div
                                            className="bg-blue-500"
                                            initial={{
                                                width: 0,
                                            }}
                                            animate={{
                                                width: `${countedPercent}%`,
                                            }}
                                            transition={{
                                                duration: 1,
                                                delay: 0.2,
                                            }}
                                        />
                                        <motion.div
                                            className="bg-amber-500"
                                            initial={{
                                                width: 0,
                                            }}
                                            animate={{
                                                width: `${pendingPercent}%`,
                                            }}
                                            transition={{
                                                duration: 1,
                                                delay: 0.4,
                                            }}
                                        />
                                    </div>
                                </div>
                            </CardContent>
                        </Card>
                    </motion.div>
                )}

                {/* Variance Stats */}
                <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                    {Object.entries(VARIANCE_CONFIG)
                        .filter(([key]) => key !== 'all')
                        .map(([key, config]) => {
                            const Icon = config.icon;
                            const count =
                                key === 'none'
                                    ? stats.exactMatch
                                    : key === 'minor'
                                        ? stats.minor
                                        : key === 'major'
                                            ? stats.major
                                            : stats.critical;
                            const isActive =
                                varianceFilter === key;
                            return (
                                <motion.button
                                    key={key}
                                    whileHover={{
                                        y: -3,
                                        scale: 1.02,
                                    }}
                                    whileTap={{
                                        scale: 0.98,
                                    }}
                                    onClick={() =>
                                        setVarianceFilter(
                                            isActive
                                                ? 'all'
                                                : (key as VarianceLevel)
                                        )
                                    }
                                    className={clsx(
                                        'rounded-xl border-2 p-4 text-center transition-all cursor-pointer',
                                        isActive
                                            ? `${config.bg} border-current shadow-md ring-2 ring-current/10`
                                            : 'bg-white border-slate-200 hover:border-slate-300 hover:shadow-sm'
                                    )}
                                    type="button"
                                >
                                    <div
                                        className={clsx(
                                            'p-2 rounded-lg w-fit mx-auto mb-2',
                                            config.bg
                                        )}
                                    >
                                        <Icon
                                            className={clsx(
                                                'w-5 h-5',
                                                config.color
                                            )}
                                        />
                                    </div>
                                    <p
                                        className={clsx(
                                            'text-2xl font-black',
                                            config.color
                                        )}
                                    >
                                        {count}
                                    </p>
                                    <p className="text-[10px] text-slate-400 uppercase mt-0.5 font-medium">
                                        {config.label}
                                    </p>
                                </motion.button>
                            );
                        })}
                </div>

                {/* Filters */}
                <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-1.5">
                    <div className="flex flex-col lg:flex-row gap-1.5">
                        <div className="relative flex-1">
                            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                            <input
                                type="text"
                                placeholder="Search products to review..."
                                value={search}
                                onChange={(e) => {
                                    setSearch(
                                        e.target.value
                                    );
                                    setPage(1);
                                }}
                                className="w-full pl-12 pr-12 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-400 focus:bg-white transition-all text-sm"
                            />
                            {search && (
                                <button
                                    onClick={() => {
                                        setSearch('');
                                        setPage(1);
                                    }}
                                    className="absolute right-3 top-1/2 -translate-y-1/2 p-1 rounded-lg text-slate-400 hover:text-slate-600"
                                    type="button"
                                >
                                    <X className="w-4 h-4" />
                                </button>
                            )}
                        </div>
                        {!isSingleStore && (
                            <select
                                value={branchId}
                                onChange={(e) => {
                                    setBranchId(
                                        e.target.value
                                    );
                                    setPage(1);
                                }}
                                className="px-4 py-3 border-2 border-slate-200 rounded-xl text-sm bg-white focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-400"
                            >
                                {branchesList.map((b) => (
                                    <option
                                        key={b.id}
                                        value={b.id}
                                    >
                                        {b.name}
                                    </option>
                                ))}
                            </select>
                        )}
                        <div className="flex items-center bg-slate-100 rounded-xl p-0.5">
                            {([
                                {
                                    value: 'counted' as ConfirmationFilter,
                                    label: 'Needs Review',
                                    icon: AlertTriangle,
                                },
                                {
                                    value: 'pending' as ConfirmationFilter,
                                    label: 'Pending',
                                    icon: Clock,
                                },
                                {
                                    value: 'all' as ConfirmationFilter,
                                    label: 'All Status',
                                    icon: Filter,
                                },
                            ]).map((f) => (
                                <button
                                    key={f.value}
                                    onClick={() => {
                                        setConfirmationFilter(
                                            f.value
                                        );
                                        setPage(1);
                                    }}
                                    className={clsx(
                                        'flex items-center gap-1 px-3 py-2 rounded-lg text-xs font-medium transition-all',
                                        confirmationFilter ===
                                            f.value
                                            ? 'bg-white text-emerald-600 shadow-sm'
                                            : 'text-slate-500 hover:text-slate-700'
                                    )}
                                    type="button"
                                >
                                    <f.icon className="w-3 h-3" />{' '}
                                    {f.label}
                                </button>
                            ))}
                        </div>
                    </div>
                </div>

                {/* Review Table */}
                {isLoading ? (
                    <div className="flex justify-center py-20">
                        <Spinner size="xl" />
                    </div>
                ) : isError ? (
                    <ErrorState
                        title="Failed to load stock"
                        onRetry={() => refetch()}
                    />
                ) : filteredItems.length === 0 ? (
                    <EmptyState
                        icon={
                            <ShieldCheck className="w-16 h-16" />
                        }
                        title={
                            confirmationFilter === 'counted'
                                ? 'All reviewed!'
                                : 'No items found'
                        }
                        description={
                            confirmationFilter === 'counted'
                                ? 'All counted items have been confirmed. Great work!'
                                : 'No items match your current filters.'
                        }
                        action={
                            confirmationFilter === 'counted'
                                ? {
                                    label: 'View All Stock',
                                    onClick: () =>
                                        setConfirmationFilter(
                                            'all'
                                        ),
                                }
                                : undefined
                        }
                    />
                ) : (
                    <Card className="overflow-hidden shadow-sm border border-slate-200">
                        {/* Selection Bar */}
                        <div className="px-4 py-3 bg-slate-50/50 border-b border-slate-100 flex items-center justify-between">
                            <div className="flex items-center gap-3">
                                <input
                                    type="checkbox"
                                    checked={
                                        selectAll &&
                                        selectableItems.length >
                                        0
                                    }
                                    onChange={handleSelectAll}
                                    className="w-4 h-4 rounded border-slate-300 text-emerald-600 focus:ring-emerald-500 cursor-pointer"
                                />
                                <p className="text-xs text-slate-500">
                                    {stats.selectable} of{' '}
                                    {totalCount} need review
                                </p>
                            </div>
                            {stats.selected > 0 && (
                                <motion.span
                                    initial={{
                                        opacity: 0,
                                        scale: 0.8,
                                    }}
                                    animate={{
                                        opacity: 1,
                                        scale: 1,
                                    }}
                                    className="text-xs font-bold text-emerald-600 bg-emerald-50 px-3 py-1 rounded-full"
                                >
                                    {stats.selected} selected
                                </motion.span>
                            )}
                        </div>

                        <div className="overflow-x-auto">
                            <table className="min-w-full divide-y divide-slate-100">
                                <thead>
                                    <tr className="bg-slate-50/50">
                                        <th className="px-4 py-3 w-10">
                                            <input
                                                type="checkbox"
                                                checked={
                                                    selectAll &&
                                                    selectableItems.length >
                                                    0
                                                }
                                                onChange={
                                                    handleSelectAll
                                                }
                                                className="w-4 h-4 rounded border-slate-300 text-emerald-600 focus:ring-emerald-500"
                                            />
                                        </th>
                                        <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase">
                                            Product
                                        </th>
                                        {!isSingleStore && (
                                            <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase hidden md:table-cell">
                                                Branch
                                            </th>
                                        )}
                                        <th className="px-4 py-3 text-right text-xs font-semibold text-slate-500 uppercase">
                                            On Hand
                                        </th>
                                        <th className="px-4 py-3 text-right text-xs font-semibold text-slate-500 uppercase">
                                            Available
                                        </th>
                                        <th className="px-4 py-3 text-center text-xs font-semibold text-slate-500 uppercase w-28">
                                            Variance
                                        </th>
                                        <th className="px-4 py-3 text-center text-xs font-semibold text-slate-500 uppercase w-28">
                                            Status
                                        </th>
                                        <th className="px-4 py-3 text-center text-xs font-semibold text-slate-500 uppercase w-32">
                                            Actions
                                        </th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-slate-50">
                                    {filteredItems.map(
                                        (item, idx) => {
                                            const varConfig =
                                                VARIANCE_CONFIG[
                                                item
                                                    .varianceLevel
                                                ];
                                            const VarIcon =
                                                varConfig.icon;
                                            const isCounted =
                                                item.confirmationStatus ===
                                                'counted';
                                            const isConfirmed =
                                                item.confirmationStatus ===
                                                'confirmed';
                                            const isPending =
                                                item.confirmationStatus ===
                                                'pending';
                                            const canSelect =
                                                !isConfirmed;

                                            return (
                                                <motion.tr
                                                    key={
                                                        item.id
                                                    }
                                                    initial={{
                                                        opacity: 0,
                                                        y: 8,
                                                    }}
                                                    animate={{
                                                        opacity: 1,
                                                        y: 0,
                                                    }}
                                                    transition={{
                                                        delay:
                                                            Math.min(
                                                                idx *
                                                                0.01,
                                                                0.3
                                                            ),
                                                    }}
                                                    className={clsx(
                                                        'transition-colors group hover:bg-slate-50',
                                                        item.isSelected &&
                                                        'bg-blue-50/40',
                                                        item.varianceLevel ===
                                                        'critical' &&
                                                        !isConfirmed &&
                                                        'bg-rose-50/20',
                                                        isConfirmed &&
                                                        'bg-emerald-50/10 opacity-70'
                                                    )}
                                                >
                                                    <td className="px-4 py-3">
                                                        {canSelect ? (
                                                            <input
                                                                type="checkbox"
                                                                checked={
                                                                    item.isSelected
                                                                }
                                                                onChange={() =>
                                                                    handleSelectOne(
                                                                        item.id,
                                                                        isConfirmed
                                                                    )
                                                                }
                                                                className="w-4 h-4 rounded border-slate-300 text-emerald-600 focus:ring-emerald-500 cursor-pointer"
                                                            />
                                                        ) : (
                                                            <CheckCircle className="w-4 h-4 text-emerald-400" />
                                                        )}
                                                    </td>
                                                    <td className="px-4 py-3">
                                                        <div
                                                            className="flex items-center gap-3 cursor-pointer"
                                                            onClick={() =>
                                                                navigate(
                                                                    `/inventory/levels/${item.productId}`
                                                                )
                                                            }
                                                        >
                                                            <div
                                                                className={clsx(
                                                                    'w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0',
                                                                    varConfig.bg
                                                                )}
                                                            >
                                                                <Package
                                                                    className={clsx(
                                                                        'w-4 h-4',
                                                                        varConfig.color
                                                                    )}
                                                                />
                                                            </div>
                                                            <div className="min-w-0">
                                                                <p className="text-sm font-semibold text-slate-900 truncate max-w-[200px] group-hover:text-emerald-600 transition-colors">
                                                                    {item.productName ||
                                                                        'Unknown'}
                                                                </p>
                                                                {item.variantName && (
                                                                    <p className="text-xs text-slate-400">
                                                                        {
                                                                            item.variantName
                                                                        }
                                                                    </p>
                                                                )}
                                                            </div>
                                                        </div>
                                                    </td>
                                                    {!isSingleStore && (
                                                        <td className="px-4 py-3 hidden md:table-cell text-sm text-slate-500">
                                                            {item.branchName ||
                                                                '—'}
                                                        </td>
                                                    )}
                                                    <td className="px-4 py-3 text-right">
                                                        <span className="text-sm font-bold text-slate-700 tabular-nums">
                                                            {formatNumber(
                                                                item.quantityOnHand
                                                            )}
                                                        </span>
                                                    </td>
                                                    <td className="px-4 py-3 text-right">
                                                        <span className="text-sm font-bold text-slate-700 tabular-nums">
                                                            {formatNumber(
                                                                item.availableQuantity
                                                            )}
                                                        </span>
                                                    </td>
                                                    <td className="px-4 py-3 text-center">
                                                        <span
                                                            className={clsx(
                                                                'inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-semibold',
                                                                varConfig.bg,
                                                                varConfig.color
                                                            )}
                                                        >
                                                            <VarIcon className="w-3 h-3" />
                                                            {item.variancePercent >
                                                                0
                                                                ? `${item.variancePercent.toFixed(1)}%`
                                                                : 'Match'}
                                                        </span>
                                                    </td>
                                                    <td className="px-4 py-3 text-center">
                                                        {isConfirmed ? (
                                                            <Badge
                                                                variant="success"
                                                                size="sm"
                                                            >
                                                                ✓
                                                                Confirmed
                                                            </Badge>
                                                        ) : isCounted ? (
                                                            <Badge
                                                                variant="info"
                                                                size="sm"
                                                            >
                                                                Counted
                                                            </Badge>
                                                        ) : (
                                                            <Badge
                                                                variant="warning"
                                                                size="sm"
                                                            >
                                                                Pending
                                                            </Badge>
                                                        )}
                                                    </td>
                                                    <td className="px-4 py-3 text-center">
                                                        <div className="flex items-center justify-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                                            {canSelect && (
                                                                <motion.button
                                                                    whileHover={{
                                                                        scale: 1.15,
                                                                    }}
                                                                    whileTap={{
                                                                        scale: 0.9,
                                                                    }}
                                                                    onClick={() =>
                                                                        handleQuickConfirm(
                                                                            item.id
                                                                        )
                                                                    }
                                                                    disabled={
                                                                        confirmMutation.isPending
                                                                    }
                                                                    className="p-1.5 rounded-lg text-emerald-600 hover:bg-emerald-50 transition-colors"
                                                                    type="button"
                                                                    title="Confirm as sellable"
                                                                >
                                                                    <CheckCheck className="w-4 h-4" />
                                                                </motion.button>
                                                            )}
                                                            <motion.button
                                                                whileHover={{
                                                                    scale: 1.15,
                                                                }}
                                                                whileTap={{
                                                                    scale: 0.9,
                                                                }}
                                                                onClick={() =>
                                                                    navigate(
                                                                        `/inventory/levels/${item.productId}`
                                                                    )
                                                                }
                                                                className="p-1.5 rounded-lg text-slate-400 hover:text-blue-600 hover:bg-blue-50 transition-colors"
                                                                type="button"
                                                                title="View stock details"
                                                            >
                                                                <Eye className="w-4 h-4" />
                                                            </motion.button>
                                                            <motion.button
                                                                whileHover={{
                                                                    scale: 1.15,
                                                                }}
                                                                whileTap={{
                                                                    scale: 0.9,
                                                                }}
                                                                onClick={() =>
                                                                    navigate(
                                                                        `/inventory/adjust?productId=${item.productId}&branchId=${item.branchId}`
                                                                    )
                                                                }
                                                                className="p-1.5 rounded-lg text-slate-400 hover:text-amber-600 hover:bg-amber-50 transition-colors"
                                                                type="button"
                                                                title="Adjust stock"
                                                            >
                                                                <RotateCcw className="w-4 h-4" />
                                                            </motion.button>
                                                            <motion.button
                                                                whileHover={{
                                                                    scale: 1.15,
                                                                }}
                                                                whileTap={{
                                                                    scale: 0.9,
                                                                }}
                                                                onClick={() =>
                                                                    navigate(
                                                                        '/inventory/ledger'
                                                                    )
                                                                }
                                                                className="p-1.5 rounded-lg text-slate-400 hover:text-indigo-600 hover:bg-indigo-50 transition-colors"
                                                                type="button"
                                                                title="View transaction history"
                                                            >
                                                                <History className="w-4 h-4" />
                                                            </motion.button>
                                                        </div>
                                                    </td>
                                                </motion.tr>
                                            );
                                        }
                                    )}
                                </tbody>
                            </table>
                        </div>

                        {/* Pagination */}
                        {totalPages > 1 && (
                            <Pagination
                                currentPage={page}
                                totalPages={totalPages}
                                totalItems={totalCount}
                                pageSize={pageSize}
                                onPageChange={handlePageChange}
                                onPageSizeChange={(size) => {
                                    setPageSize(size);
                                    setPage(1);
                                }}
                            />
                        )}
                    </Card>
                )}

                {/* Legend */}
                <div className="flex items-center justify-center gap-6 text-xs text-slate-400 flex-wrap">
                    <span className="flex items-center gap-1.5">
                        <CheckCheck className="w-3.5 h-3.5 text-emerald-500" />{' '}
                        Confirm
                    </span>
                    <span className="flex items-center gap-1.5">
                        <Eye className="w-3.5 h-3.5 text-blue-500" />{' '}
                        View Details
                    </span>
                    <span className="flex items-center gap-1.5">
                        <RotateCcw className="w-3.5 h-3.5 text-amber-500" />{' '}
                        Adjust
                    </span>
                    <span className="flex items-center gap-1.5">
                        <History className="w-3.5 h-3.5 text-indigo-500" />{' '}
                        History
                    </span>
                </div>
            </div>
        </div>
    );
}